import {HttpClient, HttpErrorResponse, HttpParams, HttpResponse} from '@angular/common/http';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {of, Subject} from 'rxjs';
import {catchError, concatMap} from 'rxjs/operators';
import {LoaderService} from '../../services/loader.service';
import {scrollerIds} from '../../constants/scroll.constants';

@Component({
  selector: 'si-infinite-scroll',
  templateUrl: './infinite-scroll.component.html',
  styleUrls: ['./infinite-scroll.component.scss']
})
export class InfiniteScrollComponent implements OnInit, OnDestroy {
  @Input() model = [] as any[];
  @Input() size = 10;
  @Input() sort: string;
  @Input() path: string;
  @Input() hideButton: boolean;

  // Inputs from goUpButton component
  @Input() buttonIcon = 'icon-up';
  @Input() buttonStyleClass: string;
  @Input() buttonBottom: string;
  @Input() scrollerId = scrollerIds.CONTAINER;

  // If we receive the values from back in an object instead of an array directly (default), we need to specify where is the array.
  @Input() pathValuesName: string;

  @Output() modelChange = new EventEmitter<any[]>();

  // Variables to manage the http request observable.
  httpRequest$ = new Subject();
  lastHttpParams: HttpParams;
  isLoading = false;
  currentPage = 0;
  totalItems = 0;

  // Variables to manage goUpButton component
  desktopUpButtonStyle: string;
  itemsLoaded = 0;
  hasScrollBar: boolean;

  // If the reset variable is initialized with 0, the component does NOT get the items for the first time. Otherwise, it DOES.
  private _reset: number;

  constructor(
    private http: HttpClient,
    private loaderService: LoaderService
  ) {
  }

  get reset() {
    return this._reset;
  }

  @Input()
  set reset(val) {
    this._reset = val;
    if (val) {
      setTimeout(() => {
        this.currentPage = 0;
        this.model = [];
        this.getItems();
      });
    }
  }

  ngOnInit(): void {
    this.initHttpRequestObservable();
  }

  initHttpRequestObservable(): void {
    if (!this.path) {
      return;
    }

    this.httpRequest$.pipe(
      concatMap(() => {
        return this.http.get(this.path, {params: this.lastHttpParams, observe: 'response'}).pipe(
          catchError(e => {
            return of(e);
          })
        );
      })
    ).subscribe(response => {
      if (response instanceof HttpErrorResponse) {
        this.processHttpErrorResponse(response);
      } else {
        this.processHttpResponse(response);
      }
    });
  }

  getItems(page = 0, size = this.size, sort = this.sort): void {
    if (!this.isLoading) {
      this.loaderService.start();
      this.isLoading = true;
    }

    this.currentPage = page;

    let httpParams = new HttpParams();
    httpParams = httpParams
      .set('page', page.toString())
      .set('size', size.toString());

    if (sort) {
      httpParams = httpParams.set('sort', sort);
    }

    this.lastHttpParams = httpParams;
    this.getHttpRequest();
  }

  getHttpRequest(): void {
    this.httpRequest$.next();
  }

  async processHttpErrorResponse(response: HttpErrorResponse): Promise<void> {
    this.setValue([]);
    this.totalItems = 0;
    this.loaderService.stop();
    this.isLoading = false;
  }

  async processHttpResponse(response: HttpResponse<any>): Promise<void> {
    if (this.pathValuesName && response.body) {
      // When the endpoint returns an object.
      // The table values are in an attribute of the object, indicated in pathValuesName.
      this.setValue(response.body[this.pathValuesName] as any[]);
    } else {
      // When the endpoint returns an array (default).
      // The table values are in the array directly.
      this.setValue(response.body as any[]);
    }

    this.totalItems = parseInt(response.headers.get('x-total-count'), 10);

    this.loaderService.stop();
    this.isLoading = false;
    this.itemsLoaded += 1;
  }

  setValue(newValue: any[]): void {
    if (!newValue) {
      newValue = [];
    }

    this.model = this.model.concat(newValue);
    this.modelChange.emit(this.model);
  }

  getMoreItems(): void {
    if (this.model?.length >= this.totalItems) {
      return;
    }

    this.getItems(this.currentPage + 1);
  }

  onChangeIsBottom(): void {
    this.getMoreItems();
  }

  onChangeScrollBar(scrollBarBool: boolean): void {
    this.hasScrollBar = scrollBarBool;
    if (!scrollBarBool) {
      this.getMoreItems();
    }
  }

  ngOnDestroy() {
    this.httpRequest$.complete();
  }
}
